import React from 'react'
import { IonGrid, IonRow } from '@ionic/react'
import classnames from 'classnames'
import { liveQuery, Subscription } from 'dexie'

import db from 'mobile_web/modules/db'
import {
	addToFavouritedKeys,
	collectionItemToFavouritedItem,
	removeFromFavouritedKeys,
} from 'mobile_web/modules/favourites/queries'
import { SectionMenuContainer as Container, CollectionItem, COLLECTION_ITEM_SUBTYPE_LIST } from 'mytours-read-common'

import { getCurrentTimestamp } from '../../../home/functions'
import NavigationService from '../../../navigation/NavigationService'
import { getCurrentLanguage, getDirMode, formatNumberStyle } from '../../../platform'
import { onLinkAction } from '../../functions'

import CardImage from '../../../common/components/card-image'
import { generateImageUrl, getScreenSize } from '../../../../common_lib/utils'
import { ImageSizePreset, ImageSizePresetMap, ScreenSize } from '../../../../common_lib/types'
import Slider from './Slider'

import '../styles/sectionMenu.less'
import { GetCollectionItemsIndexResponseData } from 'mytours-read-api-typescript'
import strings from '../../../../strings'
import { Button } from '@mytours/components'

type Props = Container.Props & Container.Actions

interface State {
	screenSize: ScreenSize
	imageWidth: number
	imageHeight: number
	showModal: boolean
	favouritesKeys: string[]
}

const INITIAL_STATE: State = {
	screenSize: ScreenSize.sm,
	imageWidth: 0,
	imageHeight: 0,
	showModal: false,
	favouritesKeys: [],
}

const dexieFavouritesKeysObservable = liveQuery(() => db.favouritedKeys.toArray())

class SectionMenu extends React.PureComponent<Props, State> {
	subscription: Subscription | undefined

	constructor(props: Props) {
		super(props)

		const screenSize = getScreenSize()

		this.state = {
			...INITIAL_STATE,
			screenSize,
			...this.getImageDimensions(screenSize),
		}
	}

	componentDidMount = () => {
		const {
			section: { collection_id: collectionId },
			accountId,
		} = this.props
		const version = getCurrentTimestamp(true)
		const language = getCurrentLanguage()

		this.props.loadMenuDetails(collectionId, version, language, accountId)

		this.setImageDimension()
		addEventListener('resize', this.setImageDimension)

		this.subscription = dexieFavouritesKeysObservable.subscribe(
			// oxlint-disable-next-line react/no-did-mount-set-state -- liveQuery delivers its first snapshot asynchronously, so this never runs during mount
			dexieFavouritesKeys => this.setState({ favouritesKeys: dexieFavouritesKeys.map(key => key.key) }),
		)
	}

	componentWillUnmount = () => {
		removeEventListener('resize', this.setImageDimension)

		if (this.subscription) {
			this.subscription.unsubscribe()
			this.subscription = null
		}
	}

	getWidth = (height: number): number => (height * 3) / 2

	getImageDimensions = (screenSize: ScreenSize = getScreenSize()): Pick<State, 'imageHeight' | 'imageWidth'> => {
		let height = 0
		switch (screenSize) {
			case ScreenSize.lg:
			case ScreenSize.md: {
				height = 318
				break
			}
			case ScreenSize.sm:
			default: {
				height = 212
				break
			}
		}
		return { imageWidth: this.getWidth(height), imageHeight: height }
	}

	setImageDimension = () => {
		const { screenSize: prevScreenSize } = this.state
		const screenSize = getScreenSize()
		if (prevScreenSize !== screenSize) {
			this.setState({ ...this.getImageDimensions(screenSize), screenSize })
		}
	}

	toggleModal = (showModal?: boolean): void => {
		this.setState(prev => ({
			showModal: showModal !== undefined ? showModal : !prev.showModal,
		}))
	}

	openModal = (collectionItem: CollectionItem): void => {
		const type = GetCollectionItemsIndexResponseData.ItemTypeEnum.Screen
		const subtype = collectionItem.item_subtype
			.split('_')
			.reverse()[0] as GetCollectionItemsIndexResponseData.ItemSubtypeEnum
		NavigationService.pushModalWindow(type, subtype, collectionItem.item_id)
	}

	renderTitleRow = (): JSX.Element | null => {
		const {
			section: { title, collection_id: collectionId },
			items,
			accountId,
		} = this.props

		const showCarousel = items && items.length > 1

		if (!title) {
			return null
		}

		return (
			<IonRow dir={getDirMode()} className="block-header header-row">
				<h2 className={classnames('subheading', getDirMode())} data-cy="story-screen-section-heading">
					{title}
				</h2>
				{showCarousel && (
					<Button
						variant="clear"
						onClick={() => {
							NavigationService.navigateToItem({
								type: COLLECTION_ITEM_SUBTYPE_LIST,
								itemId: collectionId,
								itemVersion: getCurrentTimestamp(true),
								accountId,
							})
						}}
						className="external-link"
					>
						{strings.browse_all}
					</Button>
				)}
			</IonRow>
		)
	}

	onSlideClick = (index: number) => {
		const { items } = this.props
		const collectionItem = items[index]

		const { item_subtype: itemSubtype, item_type: itemType, item_id: itemId, version, account_id } = collectionItem

		onLinkAction(
			`${itemType}_${itemSubtype}`,
			itemId,
			version,
			() => this.openModal(collectionItem),
			undefined,
			undefined,
			undefined,
			account_id,
		)
	}

	renderCarouselItem = (collectionItem: CollectionItem, isSingleItem?: boolean): React.ReactElement => {
		if (!collectionItem) {
			return undefined
		}

		const { appMetaData } = this.props
		const { imageWidth: width, imageHeight: height, favouritesKeys } = this.state

		// Keep 1x and 2x URLs on the same cover to avoid image swaps across display densities.
		const useGrid = Boolean(collectionItem.cover_image_grid_url_display || collectionItem.cover_image_grid?.file)
		const coverImage = useGrid ? collectionItem.cover_image_grid : collectionItem.cover_image
		const coverImageUrlDisplay = useGrid
			? collectionItem.cover_image_grid_url_display
			: collectionItem.cover_image_url_display

		const coverImageFocalPoint = coverImage?.file?.focal_point
		const coverImageCrop = coverImageFocalPoint && (coverImageFocalPoint[0] !== 0.5 || coverImageFocalPoint[1] !== 0.5)
		const imageOptions = { crop: coverImageCrop, focalPoint: coverImageFocalPoint }
		const preset = ImageSizePresetMap[ImageSizePreset.Row]

		const imageUrl =
			coverImageUrlDisplay ||
			(coverImage?.file
				? generateImageUrl(coverImage.file, { imageSizePreset: ImageSizePreset.Row, ...imageOptions })
				: undefined)
		const imageUrl2x = coverImage?.file
			? generateImageUrl(coverImage.file, { width: preset.width * 2, height: preset.height * 2, ...imageOptions })
			: undefined

		const key = `${collectionItem.item_id}-${collectionItem.item_type}`
		const isFavourited = favouritesKeys.includes(key)
		const toggleFavorite: React.MouseEventHandler<HTMLButtonElement> = e => {
			e.stopPropagation()
			const favouritedItem = collectionItemToFavouritedItem(collectionItem)
			if (!isFavourited) {
				addToFavouritedKeys(key, favouritedItem)
			} else {
				removeFromFavouritedKeys(key)
			}
		}

		return (
			<CardImage
				// Explicit width and height, so the card keeps its 3:2 shape rather than the
				// aspect-ratio the `grid` variant would impose.
				style={{
					maxWidth: formatNumberStyle(width),
					height: formatNumberStyle(height),
					width: formatNumberStyle(width),
				}}
				image={imageUrl}
				image2x={imageUrl2x}
				label={collectionItem.title}
				itemId={collectionItem.item_id}
				itemType={collectionItem.item_type}
				item={collectionItem}
				appMetaData={appMetaData}
				toggleFavorite={toggleFavorite}
				isFavourited={isFavourited}
				// Carousel wrappers handle clicks; a button role would hide the nested favourite toggle.
				{...(isSingleItem ? { onClick: () => this.onSlideClick(0) } : {})}
			/>
		)
	}

	renderContent = () => {
		const { items } = this.props

		const showCarousel = items && items.length > 1

		return showCarousel ? (
			<Slider
				className="block-content"
				sliderClassName="section-menu-slider"
				renderItem={section => this.renderCarouselItem(section as CollectionItem)}
				data={items}
				initialSlide={0}
				onSlideClick={this.onSlideClick}
				slideOptions={{
					shortSwipes: true,
					slidesPerView: 'auto',
				}}
				enableControls={true}
				data-cy="story-screen-section-content"
			/>
		) : (
			<IonRow className="block-content single-menu-item" data-cy="story-screen-section-content">
				{this.renderCarouselItem(items[0], true)}
			</IonRow>
		)
	}

	render = () => {
		const { items } = this.props

		if (items.length === 0) {
			return <div />
		}

		const showCarousel = items && items.length > 1

		return (
			<IonGrid
				className={classnames('section-menu-grid', {
					'single-item': !showCarousel,
				})}
				dir={getDirMode()}
			>
				{this.renderTitleRow()}
				{this.renderContent()}
			</IonGrid>
		)
	}
}

const SectionMenuComponent = Container.default(SectionMenu)
export default SectionMenuComponent
